import React, { useState, useEffect } from 'react'
import { fetchAnalytics } from '../services/api'
import { 
  HiChartBar, 
  HiTrophy, 
  HiUsers, 
  HiChatBubbleLeftRight,
  HiArrowTrendingUp,
  HiClock,
  HiSparkles
} from 'react-icons/hi2'
import MetricCard from '../components/MetricCard'

interface AnalyticsData {
  prediction_statistics: {
    total_predictions: number
    correct_predictions: number
    incorrect_predictions: number
    predictions_pending_outcome: number
    accuracy_percentage: number
    breakdown_by_strength: Array<{
      strength: string
      correct: number
      total: number
      accuracy: number
    }>
  }
  reddit_comments_statistics: {
    total_comments: number
    games_with_comments: number
    avg_comments_per_game: number
    sentiment_breakdown: Record<string, number>
    fan_type_breakdown: Record<string, number>
  }
  team_statistics: {
    top_teams_by_comments: Array<{
      team: string
      comment_count: number
    }>
    top_teams_by_games: Array<{
      team: string
      game_count: number
    }>
    team_sentiment_stats: Array<{
      team: string
      comment_count: number
      avg_sentiment: number
      avg_normalized_sentiment: number
    }>
  }
  game_statistics: {
    total_games: number
    finished_games: number
    live_games: number
    upcoming_games: number
    games_with_scores: number
    games_with_comments: number
  }
  time_based_statistics: {
    comments_over_time: Array<{
      date: string
      count: number
    }>
    predictions_over_time: Array<{
      date: string
      count: number
    }>
  }
}

const AnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAnalytics()
  }, [])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await fetchAnalytics()
      setAnalytics(data)
    } catch (err) {
      setError('Failed to load analytics')
      console.error('Error loading analytics:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    )
  }

  if (error || !analytics) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error || 'No analytics data available'}</div>
        <button 
          onClick={loadAnalytics}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  const { prediction_statistics, reddit_comments_statistics, team_statistics, game_statistics } = analytics

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 flex items-center justify-center">
          <HiChartBar className="h-9 w-9 sm:h-10 sm:w-10 mr-3 text-orange-400" />
          Analytics Dashboard
        </h1>
        <p className="text-base sm:text-xl text-gray-400 mb-6 sm:mb-8">
          Comprehensive statistics and insights about fanalyzes.com
        </p>
      </div>

      {/* Prediction Statistics Section */}
      <div className="card">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center">
          <HiTrophy className="h-5 w-5 sm:h-6 sm:w-6 mr-3 text-orange-400" />
          Prediction Statistics
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <MetricCard
            value={prediction_statistics.total_predictions}
            label="Total Predictions"
            color="info"
          />
          <MetricCard
            value={prediction_statistics.correct_predictions}
            label="Correct Predictions"
            color="success"
          />
          <MetricCard
            value={prediction_statistics.incorrect_predictions}
            label="Incorrect Predictions"
            color="danger"
          />
          <MetricCard
            value={`${prediction_statistics.accuracy_percentage.toFixed(1)}%`}
            label="Accuracy Rate"
            color={prediction_statistics.accuracy_percentage >= 50 ? 'success' : 'warning'}
            size="lg"
          />
        </div>

        {prediction_statistics.breakdown_by_strength.length > 0 && (
          <div className="mt-6">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Accuracy by Prediction Strength</h3>
            <div className="space-y-3">
              {prediction_statistics.breakdown_by_strength.map((breakdown) => (
                <div key={breakdown.strength} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white capitalize">{breakdown.strength.replace('_', ' ')}</span>
                    <span className="text-orange-400 font-semibold">
                      {breakdown.total > 0 ? `${breakdown.accuracy.toFixed(1)}%` : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <span>{breakdown.correct} correct</span>
                    <span>•</span>
                    <span>{breakdown.total} total</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {prediction_statistics.predictions_pending_outcome > 0 && (
          <div className="mt-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
            <div className="flex items-center space-x-2 text-gray-300">
              <HiClock className="h-5 w-5 text-orange-400" />
              <span className="text-sm sm:text-base">
                {prediction_statistics.predictions_pending_outcome} predictions waiting for game outcomes
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Reddit Comments Statistics Section */}
      <div className="card">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center">
          <HiChatBubbleLeftRight className="h-5 w-5 sm:h-6 sm:w-6 mr-3 text-orange-400" />
          Reddit Comments Statistics
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
          <MetricCard
            value={reddit_comments_statistics.total_comments.toLocaleString()}
            label="Total Comments"
            color="primary"
            size="lg"
          />
          <MetricCard
            value={reddit_comments_statistics.games_with_comments}
            label="Games with Comments"
            color="info"
          />
          <MetricCard
            value={reddit_comments_statistics.avg_comments_per_game.toFixed(1)}
            label="Avg Comments per Game"
            color="info"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Sentiment Breakdown */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Sentiment Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(reddit_comments_statistics.sentiment_breakdown).map(([sentiment, count]) => (
                <div key={sentiment} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-white capitalize">{sentiment}</span>
                    <span className="text-orange-400 font-semibold">{count.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fan Type Breakdown */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Fan Type Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(reddit_comments_statistics.fan_type_breakdown).map(([fanType, count]) => (
                <div key={fanType} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-white">
                      {fanType === 'HOME_FAN' ? 'Home Fans' : 
                       fanType === 'AWAY_FAN' ? 'Away Fans' : 
                       'Neutral'}
                    </span>
                    <span className="text-orange-400 font-semibold">{count.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Team Statistics Section */}
      <div className="card">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center">
          <HiUsers className="h-5 w-5 sm:h-6 sm:w-6 mr-3 text-orange-400" />
          Team Statistics
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Teams by Comments */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center">
              <HiSparkles className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-400" />
              Top Teams by Comments
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {team_statistics.top_teams_by_comments.length > 0 ? (
                team_statistics.top_teams_by_comments.map((team, index) => (
                  <div key={team.team} className="bg-gray-800 p-3 rounded-lg border border-gray-700 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-orange-400 font-bold w-6">#{index + 1}</span>
                      <span className="font-medium text-white">{team.team}</span>
                    </div>
                    <span className="text-orange-400 font-semibold">{team.comment_count.toLocaleString()}</span>
                  </div>
                ))
              ) : (
                <div className="text-gray-400 text-center py-4">No team data available</div>
              )}
            </div>
          </div>

          {/* Top Teams by Games */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center">
              <HiTrophy className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-orange-400" />
              Top Teams by Games Analyzed
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {team_statistics.top_teams_by_games.length > 0 ? (
                team_statistics.top_teams_by_games.map((team, index) => (
                  <div key={team.team} className="bg-gray-800 p-3 rounded-lg border border-gray-700 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-orange-400 font-bold w-6">#{index + 1}</span>
                      <span className="font-medium text-white">{team.team}</span>
                    </div>
                    <span className="text-orange-400 font-semibold">{team.game_count}</span>
                  </div>
                ))
              ) : (
                <div className="text-gray-400 text-center py-4">No team data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Team Sentiment Stats */}
        {team_statistics.team_sentiment_stats.length > 0 && (
          <div className="mt-6">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Team Average Sentiment</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {team_statistics.team_sentiment_stats.map((team) => (
                <div key={team.team} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white">{team.team}</span>
                    <span className="text-gray-400 text-sm">{team.comment_count} comments</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Avg Sentiment: </span>
                      <span className="text-orange-400 font-semibold">
                        {team.avg_sentiment.toFixed(3)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Game Statistics Section */}
      <div className="card">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center">
          <HiArrowTrendingUp className="h-5 w-5 sm:h-6 sm:w-6 mr-3 text-orange-400" />
          Game Statistics
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard
            value={game_statistics.total_games}
            label="Total Games"
            color="info"
          />
          <MetricCard
            value={game_statistics.finished_games}
            label="Finished"
            color="success"
          />
          <MetricCard
            value={game_statistics.live_games}
            label="Live"
            color="warning"
          />
          <MetricCard
            value={game_statistics.upcoming_games}
            label="Upcoming"
            color="info"
          />
          <MetricCard
            value={game_statistics.games_with_scores}
            label="With Scores"
            color="success"
          />
          <MetricCard
            value={game_statistics.games_with_comments}
            label="With Comments"
            color="primary"
          />
        </div>
      </div>

      {/* Refresh Button */}
      <div className="text-center">
        <button 
          onClick={loadAnalytics}
          className="btn-primary"
        >
          Refresh Analytics
        </button>
      </div>
    </div>
  )
}

export default AnalyticsPage

